import _ from 'lodash'
import type {
  TaskType,
  PrefetchApiFallbackType,
  InnerFuncParamType,
  PrefetchApiPropsType,
} from '../index'
import { createTaskConfigList } from './common'
import { handleTasks } from './handleTasks'
import { PrefetchApiConfigType } from '../index'

const PREFETCH_API_NAME = '__AL_EX_prefetchApi_qXdTr8HCwJUQXCGJ'
const FALLBACK_API_NAME = '__AL_EX_prefetchApi_fallback_qXdTr8HCwJUQXCGJ'
const PREFETCH_API_STORE_KEY = '__AL_EX_prefetchApi_result_qXdTr8HCwJUQXCGJ'

const EXTRA_GLOBAL_PROPS_STORE_KEY = Symbol(
  'next/al-ex/prefetchApi/browser#EXTRA_GLOBAL_PROPS_STORE_KEY'
)

export function handleOnClientAppGlobal(
  param: InnerFuncParamType
): PrefetchApiPropsType {
  if (!process.browser) {
    throw new Error('仅支持在 browser 执行')
  }

  if (!param.ctx.lifecycleStatus.isExportMode) {
    // ssr 时，直接返回，状态不会再变化
    requestAnimationFrame(() => {
      // 总是异步的 onComplete
      if (param.onComplete) {
        param.onComplete()
      }
    })
    const globalProps = callClientGetInitialProps().global
    ;(window as any)[EXTRA_GLOBAL_PROPS_STORE_KEY] = globalProps // 额外保留 globalProps 引用
    return globalProps
  } else {
    // export 时，均监听状态变化
    const globalTasks = _.pickBy(
      callClientGetCurrentTasks(),
      (task) => task.category === 'global'
    )
    const globalProps = handleTasksToProps(
      globalTasks,
      param,
      () => {
        throw new Error('global 不涉及 callback，无需提供')
      },
      () => {
        throw new Error('global 不涉及 callback，无需提供')
      }
    )
    ;(window as any)[EXTRA_GLOBAL_PROPS_STORE_KEY] = globalProps // 额外保留 globalProps 引用
    return globalProps
  }
}

export function handleOnClientFirstPage(
  param: InnerFuncParamType
): PrefetchApiPropsType {
  if (!process.browser) {
    throw new Error('仅支持在 browser 执行')
  }
  if (!param.ctx.lifecycleStatus.isFirstPage) {
    throw new Error('仅支持在首页的 client 执行')
  }

  if (!param.ctx.lifecycleStatus.isExportMode) {
    // ssr 时，直接返回，状态不会再变化
    requestAnimationFrame(() => {
      // 总是异步的 onComplete
      if (param.onComplete) {
        param.onComplete()
      }
    })
    return callClientGetInitialProps().page
  }

  const globalProps = getGlobalProps()
  const pageTasks = _.pickBy(
    callClientGetCurrentTasks(),
    (task) => task.category === 'page'
  )

  const pageProps = handleTasksToProps(
    pageTasks,
    param,
    () => ({ ...globalProps, ...pageProps }),
    () => [...Object.keys(globalProps), ...Object.keys(pageProps)]
  )
  return pageProps
}

export function handleOnClientRouteChangePage(
  param: InnerFuncParamType
): PrefetchApiPropsType {
  if (!process.browser) {
    throw new Error('仅支持在 browser 执行')
  }
  if (param.ctx.lifecycleStatus.isFirstPage) {
    throw new Error('仅支持非首页的 css routeChange 执行')
  }

  const globalProps = getGlobalProps()

  // 从当前 page 配置生成 page 任务
  const pageTaskConf = createTaskConfigList(undefined, param.pagePrefetchApi)
  Object.keys(pageTaskConf).forEach((propKey) => {
    if (propKey in globalProps) {
      throw new Error('业务 propKey，不能覆盖 global 任务')
    }
  })
  const pageTasks = callClientStartApi(pageTaskConf, param.pageMock)

  // 返回页面渲染所需的 props，跟踪任务流转状态，变化时通过 onPropChange 通知
  const pageProps = handleTasksToProps(
    pageTasks,
    param,
    () => ({ ...globalProps, ...pageProps }),
    () => [...Object.keys(globalProps), ...Object.keys(pageProps)]
  )
  return pageProps
}

function handleTasksToProps(
  taskMap: { [propKey: string]: TaskType },
  param: InnerFuncParamType,
  getGlobalAndPageProps: () => PrefetchApiPropsType,
  getGlobalAndPagePropKeys: () => string[]
) {
  // 生成 props，以便页面先行渲染
  // 当 callback 增加了新 prop 时，resultProps 将会同步增加
  const resultProps: PrefetchApiPropsType = {}
  Object.keys(taskMap).forEach((propKey) => {
    // handleTask 保证了已确定的初始状态，将在 handleTask() 同步时序上触发 onChange
    // 这意味着，最终外部调用同步时序完成时，状态是正确的
    // 不会发生不必要的多次状态流转
    resultProps[propKey] = {
      status: 'INITIAL',
      value: undefined,
      isReady: false,
    }
  })
  // 初始状态，不触发 onPropChange
  // 仅初始状态之后发生改变的，才触发 onPropChange

  function handleOnPropChange(propKey: string) {
    if (param.onPropChange) {
      param.onPropChange(propKey)
    }
  }

  // 任务流转，但不阻塞
  let isSyncTiming = true
  handleTasks(
    taskMap,
    () => {},
    () => {
      if (param.onComplete) {
        param.onComplete()
      }
    },
    (
      propKey: string,
      status: 'INITIAL' | 'LOADING' | 'CANCEL' | 'COMPLETE',
      data?: any
    ) => {
      const isReady = status === 'CANCEL' || status === 'COMPLETE'
      const prop = resultProps[propKey]
      if (isSyncTiming) {
        // 同步时序时，实际为初始状态更新
        prop.status = status
        prop.value = data
        prop.isReady = isReady
      } else {
        // 非同步时序，正式的状态更新
        // 总是创建新实例，若外部需实现浅比较，自行跟踪变化
        if (status === 'INITIAL' && !!resultProps[propKey]) {
          // 仅 callback 生成的新 propKey 会产生
          // handleCallback 已检查，不允许 callback 返回的项覆盖原 props
          throw new Error('BUG')
        }
        resultProps[propKey] = {
          status: status,
          value: data,
          isReady: isReady,
        }
        // 仅初始状态之后发生改变的，才触发 onPropChange
        handleOnPropChange(propKey)
      }
    },
    getGlobalAndPagePropKeys,
    !!param.pageFallback,
    (task: TaskType) => {
      if (!param.pageFallback) {
        throw new Error('BUG')
      }
      return callClientStartFallback(task, param.pageFallback)
    },
    !!param.pageCallback,
    (task: TaskType) => {
      if (!param.pageCallback) {
        throw new Error('BUG')
      }
      // 使用副本，防止业务预期外修改
      // 不能直接使用 resultProps，其制式由 taskMap 指定的部分 props
      return param.pageCallback(
        { ...getGlobalAndPageProps() },
        task.propKey,
        param.ctx
      )
    },
    param.convertRawResponse
  ).then(
    () => {},
    (err) => {
      // 异步函数，但不 await 等待 或 return promise
      // 此函数为同步，此处抛出的错误会展示在console、被sentry采集，但不会中断程序
      throw err
    }
  )
  isSyncTiming = false

  return resultProps
}

// 见 _prefetchApi.js.txt
function callClientStartFallback(
  task: TaskType,
  fallback: PrefetchApiFallbackType
) {
  ;(window as any)[FALLBACK_API_NAME](task, fallback)
  return new Promise((resolve) => {
    ;(task.fallback as Promise<void>).then(resolve)
  }) as Promise<void>
}

// 见 _prefetchApi.js.txt
function callClientStartApi(
  configMap: PrefetchApiConfigType,
  mockData?: object
): { [propKey: string]: TaskType } {
  // 启动，写入 store。存在同名 propKey 时覆盖，外部自行确保逻辑正确
  ;((window as any)[PREFETCH_API_NAME] as any)(configMap, mockData)
  return _.pick(callClientGetCurrentTasks(), Object.keys(configMap))
}

// 见 _prefetchApi.js.txt
function callClientGetCurrentTasks() {
  // 使用副本
  return {
    ...((window as any)[PREFETCH_API_STORE_KEY] as {
      [propKey: string]: TaskType
    }),
  }
}

// 仅 ssr 首页可用
function callClientGetInitialProps() {
  const { pageProps } = window.__NEXT_DATA__.props
  const {
    __AL_EX_FW_prefetchApiInitialGlobalPropKeys,
    __AL_EX_FW_prefetchApiInitialPagePropKeys,
  } = pageProps
  return {
    global: _.pick(pageProps, __AL_EX_FW_prefetchApiInitialGlobalPropKeys),
    page: _.pick(pageProps, __AL_EX_FW_prefetchApiInitialPagePropKeys),
  }
}

function getGlobalProps() {
  // ssr / export 均可用
  return (window as any)[EXTRA_GLOBAL_PROPS_STORE_KEY] as PrefetchApiPropsType
}
